import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './GuessTheMeaning.module.css';
import BackButton from '../../common/BackButton/BackButton';
import { apiRequest } from '../../../config/api';

const GuessTheMeaning = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [words, setWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [userGuess, setUserGuess] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [showDefinition, setShowDefinition] = useState(false);
  const [wordMasteredMessage, setWordMasteredMessage] = useState(null);
  const [noWordsFound, setNoWordsFound] = useState(false);
  const [noWordsMessage, setNoWordsMessage] = useState('');
  // New state for exercise type
  const [exerciseType, setExerciseType] = useState('guess_meaning');
  // State for mnemonic options
  const [selectedMnemonic, setSelectedMnemonic] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const isFetched = useRef(false);
  
  // Get the current word
  const currentWord = words[currentWordIndex] || {};
  
  // Set defaults if no word is loaded yet
  const displayWord = currentWord.word || "Loading...";
  const displayPos = currentWord.partOfSpeech || "noun";
  const displayDefinition = currentWord.definition || "Loading definition...";
  
  // Get mnemonic options from the current word
  const mnemonicOptions = currentWord.mnemonicOptions || [];
  
  useEffect(() => {
    const element = document.querySelector('.bg-main');
    if (element) {
      element.style.background = 'linear-gradient(135deg, #1a237e, #3949ab)';
    }
    
    // Fetch words when component mounts
    if (!isFetched.current) {
      fetchWords();
      isFetched.current = true;
    }
    
    return () => {
      if (element) {
        element.style.background = '#f5f5f5';
      }
    };
  }, []);
  
  const fetchWords = async () => {
    try {
      setLoading(true);
      setError(null);
      setNoWordsFound(false);
      
      const { state } = location;
      const difficulty = state?.difficulty || 4;
      const listId = state?.listId;
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        mode: 'recall',
        practiceType: 'guess_meaning',
        difficulty,
        count: 10,
        theme: state?.theme || 'general'
      });
      
      if (listId) {
        queryParams.append('listId', listId);
      }
      
      const response = await apiRequest(`/vocabulary/flashcards?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      // Check if we have words to practice
      if (response.noWordsFound) {
        setNoWordsFound(true);
        setNoWordsMessage(response.message || "No words found to practice.");
        setLoading(false);
        return;
      }
      
      // Assign exercise type to each word and prepare mnemonic options
      const wordsWithExerciseType = response.words.map(word => {
        // Randomly assign exercise type to each word
        const wordExerciseType = Math.random() > 0.5 ? 'guess_meaning' : 'match_mnemonic';
        
        // If it's a mnemonic exercise, prepare the options
        let options = [];
        if (wordExerciseType === 'match_mnemonic') {
          const correctMnemonic = word.mnemonics[0]?.description || "No mnemonic available";
          const wrongMnemonics = word.wrongMnemonics || [];
          
          options = [
            { text: correctMnemonic, isCorrect: true },
            ...wrongMnemonics.slice(0, 3).map(text => ({ text, isCorrect: false }))
          ].sort(() => 0.5 - Math.random());
        }
        
        return {
          ...word,
          exerciseType: wordExerciseType,
          mnemonicOptions: options
        };
      });
      
      setWords(wordsWithExerciseType);
      setSessionId(response.sessionId);
      setCurrentWordIndex(0);
      setStartTime(Date.now());
      
      // Set the initial exercise type based on the first word
      if (wordsWithExerciseType.length > 0) {
        setExerciseType(wordsWithExerciseType[0].exerciseType);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching words:', error);
      setError('Failed to load vocabulary words. Please try again.');
      setLoading(false);
    }
  };
  
  useEffect(() => {
    // Only run this effect when currentWordIndex changes, not on initial mount
    if (words.length === 0) return;
    
    const currentWord = words[currentWordIndex];
    
    // Reset state when moving to a new word
    setUserGuess('');
    setEvaluation(null);
    setShowDefinition(false);
    setStartTime(Date.now());
    setSelectedMnemonic(null);
    
    // Set exercise type from the current word
    setExerciseType(currentWord.exerciseType);
  }, [currentWordIndex, words.length]);
  
  const handleSubmitGuess = async () => {
    if (!userGuess.trim() || !sessionId || !currentWord.id) return;
    
    try {
      setLoading(true);
      const timeSpent = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
      
      const response = await apiRequest('/vocabulary/flashcards/evaluate-guess', {
        method: 'POST',
        body: JSON.stringify({
          sessionId,
          wordId: currentWord.id,
          userGuess,
          timeSpent
        })
      });
      
      setEvaluation(response);
      setShowDefinition(true);
      setLoading(false);
      
      // Check if the word was mastered
      if (response.wordMastered) {
        setWordMasteredMessage(response.masteryMessage || "Word mastered! Removed from review list.");
        
        // Clear the message after 3 seconds
        setTimeout(() => {
          setWordMasteredMessage(null);
        }, 3000);
      }
    } catch (error) {
      console.error('Error evaluating guess:', error);
      setError('Failed to evaluate your answer. Please try again.');
      setLoading(false);
    }
  };
  
  const handleSelectMnemonic = async (option, index) => {
    if (!sessionId || !currentWord.id || selectedMnemonic !== null) return;
    
    try {
      setLoading(true);
      const timeSpent = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
      
      // Mark the selected option
      setSelectedMnemonic(index);
      
      // Create a simplified evaluation object for mnemonic matching
      const isCorrect = option.isCorrect;
      
      // Update the session progress using the new endpoint
      const response = await apiRequest('/vocabulary/flashcards/evaluate-mnemonic', {
        method: 'POST',
        body: JSON.stringify({
          sessionId,
          wordId: currentWord.id,
          isCorrect,
          timeSpent
        })
      });
      
      setEvaluation(response);
      setShowDefinition(true);
      setLoading(false);
      
      // Check if the word was mastered
      if (response.wordMastered) {
        setWordMasteredMessage(response.masteryMessage || "Word mastered! Removed from review list.");
        
        // Clear the message after 3 seconds
        setTimeout(() => {
          setWordMasteredMessage(null);
        }, 3000);
      }
    } catch (error) {
      console.error('Error evaluating mnemonic selection:', error);
      setError('Failed to evaluate your answer. Please try again.');
      setLoading(false);
    }
  };
  
  const moveToNextWord = () => {
    if (currentWordIndex < words.length - 1) {
      setCurrentWordIndex(currentWordIndex + 1);
    } else {
      // End of session
      navigate('/', { 
        state: { 
          sessionCompleted: true,
          mode: 'guess_meaning'
        } 
      });
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && exerciseType === 'guess_meaning') {
      if (!evaluation) {
        handleSubmitGuess();
      } else {
        moveToNextWord();
      }
    }
  };
  
  const getScoreColor = (score) => {
    if (score >= 86) return '#4CAF50'; // Green
    if (score >= 61) return '#8BC34A'; // Light Green
    if (score >= 31) return '#FFC107'; // Amber
    return '#F44336'; // Red
  };
  
  const getMnemonicOptionClass = (index) => {
    if (selectedMnemonic === null) return styles.mnemonicOption;
    
    if (index === selectedMnemonic) {
      return mnemonicOptions[index].isCorrect 
        ? styles.mnemonicOptionCorrect 
        : styles.mnemonicOptionIncorrect;
    }
    
    if (mnemonicOptions[index].isCorrect) {
      return styles.mnemonicOptionCorrectAnswer;
    }
    
    return styles.mnemonicOption;
  };
  
  const handleLearnNewWords = () => {
    navigate('/vocabulary/learn_practice/flashcards', { 
      state: { 
        mode: 'learn_practice', 
        subMode: 'learn',
        difficulty: location.state?.difficulty || 4
      } 
    });
  };
  
  if (loading && words.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <p>Loading vocabulary words...</p>
      </div>
    );
  }
  
  if (noWordsFound) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorCard}>
          <h3 className={styles.errorTitle}>No Words to Practice</h3>
          <p className={styles.errorMessage}>{noWordsMessage}</p>
          <button 
            className={styles.learnButton}
            onClick={handleLearnNewWords}
          >
            Learn New Words Instead
          </button>
        </div>
        <BackButton />
      </div>
    );
  }
  
  if (error && words.length === 0) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>{error}</p>
        <button 
          className={styles.retryButton}
          onClick={fetchWords}
        >
          Try Again
        </button>
        <BackButton />
      </div>
    );
  }
  
  return (
    <div className={styles.container}>
      {wordMasteredMessage && (
        <div className={styles.masteredMessage}>
          {wordMasteredMessage}
        </div>
      )}
      
      <div className={styles.card}>
        <div className={styles.wordContainer}>
          <h2 className={styles.word}>{displayWord}</h2>
          <span className={styles.partOfSpeech}>{displayPos}</span>
        </div>
        
        {exerciseType === 'guess_meaning' ? (
          <div className={styles.guessContainer}>
            <h3 className={styles.question}>What do you think this word means?</h3>
            
            <textarea
              className={styles.guessInput}
              value={userGuess}
              onChange={(e) => setUserGuess(e.target.value)}
              placeholder="Type your definition here..."
              disabled={!!evaluation}
              onKeyPress={handleKeyPress}
            />
            
            {!evaluation ? (
              <button 
                className={styles.submitButton}
                onClick={handleSubmitGuess}
                disabled={!userGuess.trim()}
              >
                {loading ? (
                  <div className={styles.loader}></div>
                ) : (
                  'Submit'
                )}
              </button>
            ) : (
              <button 
                className={styles.nextButton}
                onClick={moveToNextWord}
              >
                Next Word
              </button>
            )}
          </div>
        ) : (
          <div className={styles.mnemonicContainer}>
            <h3 className={styles.question}>Which mnemonic helps you remember this word?</h3>
            
            <div className={styles.mnemonicOptions}>
              {mnemonicOptions.map((option, index) => (
                <div 
                  key={index}
                  className={getMnemonicOptionClass(index)}
                  onClick={() => !evaluation && handleSelectMnemonic(option, index)}
                >
                  {option.text}
                </div>
              ))}
            </div>
            
            {evaluation && (
              <button 
                className={styles.nextButton}
                onClick={moveToNextWord}
              >
                Next Word
              </button>
            )}
          </div>
        )}
        
        {showDefinition && (
          <div className={styles.resultContainer}>
            <div className={styles.scoreContainer}>
              <div 
                className={styles.scoreCircle}
                style={{ backgroundColor: getScoreColor(evaluation.score) }}
              >
                {evaluation.score}
              </div>
              <span className={styles.scoreLabel}>
                {evaluation.isCorrect ? 'Correct!' : 'Try Again'}
              </span>
            </div>
            
            <div className={styles.feedbackContainer}>
              <p className={styles.feedback}>{evaluation.feedback}</p>
            </div>
            
            <div className={styles.definitionContainer}>
              <h4 className={styles.definitionLabel}>Actual Definition:</h4>
              <p className={styles.definition}>{displayDefinition}</p>
            </div>
          </div>
        )}
      </div>
      
      <BackButton />
    </div>
  );
};

export default GuessTheMeaning; 